import { AUTH_SESSION_STORAGE_KEY } from '../../auth/AuthContext';
import { AuthInterceptedError } from '../ErrorHandler/globalFetchInterceptor';
import { buildApiUrl } from './apiBaseUrl';

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { token?: string }).token ?? null;
  } catch {
    return null;
  }
}

function generateTraceId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Centralized authenticated fetch client.
 *
 * Automatically:
 * - Injects the Bearer token from sessionStorage
 * - Attaches an x-trace-id header for tracing
 * - Propagates AuthInterceptedError silently (the global error dialog handles it)
 * - Throws a typed error with `status` and `code` for other failures
 */
type ApiFetchResponseType = 'auto' | 'blob';

type ApiFetchInit = RequestInit & {
  responseType?: ApiFetchResponseType;
  timeoutMs?: number;
};

export type ApiFetchResult<T> = {
  data: T;
  traceId: string;
};

type ApiErrorBody = {
  message?: string;
  code?: string;
  traceId?: string;
};

type ApiFetchError = Error & {
  status?: number;
  code?: string;
  traceId?: string;
};

function readResponseTraceId(
  response: Pick<Response, 'headers'> | { headers?: Headers | { get?: (name: string) => string | null } },
  fallbackTraceId: string,
) {
  const responseTraceId = response.headers?.get?.('x-trace-id')?.trim();
  return responseTraceId || fallbackTraceId;
}

function getRequestSignal(signal: AbortSignal | null | undefined, timeoutSignal: AbortSignal | undefined) {
  if (!timeoutSignal) {
    return signal;
  }

  if (!signal) {
    return timeoutSignal;
  }

  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function'
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;
}

function attachTraceId(error: unknown, traceId: string): never {
  if (typeof error === 'object' && error !== null) {
    throw Object.assign(error, { traceId });
  }

  const wrappedError = new Error(String(error)) as ApiFetchError;
  wrappedError.traceId = traceId;
  throw wrappedError;
}

export async function apiFetchWithMeta<T>(
  path: string,
  init?: ApiFetchInit
): Promise<ApiFetchResult<T>> {
  const token = getToken();
  const { responseType = 'auto', timeoutMs, ...fetchInit } = init ?? {};
  const requestTraceId = generateTraceId();
  const timeoutController = typeof timeoutMs === 'number' ? new AbortController() : undefined;
  const timeoutId = timeoutController
    ? setTimeout(() => {return timeoutController.abort()}, timeoutMs)
    : undefined;
  const signal = getRequestSignal(fetchInit.signal, timeoutController?.signal);

  const url = buildApiUrl(path);

  try {
    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchInit,
        headers: {
          'Content-Type': 'application/json',
          'x-trace-id': requestTraceId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(fetchInit.headers ?? {}),
        },
        signal,
      });
    } catch (err) {
      if (err instanceof AuthInterceptedError) {
        throw err;
      }
      attachTraceId(err, requestTraceId);
    }

    if (!response.ok) {
      const body = (await response.json().catch(() => {
        return {};
      })) as ApiErrorBody;
      const err = new Error(
        body.message ?? `Request failed: ${response.status}`
      ) as ApiFetchError;
      err.status = response.status;
      err.code = body.code;
      err.traceId = body.traceId?.trim() || readResponseTraceId(response, requestTraceId);
      throw err;
    }

    const traceId = readResponseTraceId(response, requestTraceId);

    if (response.status === 204) {
      return { data: null as T, traceId };
    }

    if (responseType === 'blob') {
      return {
        data: await response.blob() as T,
        traceId,
      };
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    if (contentType.includes('application/json')) {
      return {
        data: await response.json() as T,
        traceId,
      };
    }

    return {
      data: await response.text() as T,
      traceId,
    };
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export async function apiFetch<T>(
  path: string,
  init?: ApiFetchInit
): Promise<T> {
  const result = await apiFetchWithMeta<T>(path, init);
  return result.data;
}
